import React, { useState } from 'react';
import { Send, Mail, Check, AlertCircle, Coffee } from 'lucide-react';
import AdminNav from './AdminNav';

export default function EmailTest() {
  const [formData, setFormData] = useState({
    to: 'hi@birthday.coffee',
    recipientName: 'Birthday Coffee Team',
    senderName: 'Test Sender',
    amount: 10,
    message: 'Testing our awesome Birthday Coffee email system! 🎂☕',
    giftCode: `COFFEE-${Math.random().toString(36).substring(2, 10).toUpperCase()}`
  });

  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string; data?: any } | null>(null);

  const sendTestEmail = async () => {
    setSending(true);
    setResult(null);

    try {
      const response = await fetch('https://birthday-coffee-email.b-9f2.workers.dev', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setResult({
          success: true,
          message: 'Email sent successfully!',
          data
        });
      } else {
        setResult({
          success: false,
          message: data.error || 'Failed to send email',
          data
        });
      }
    } catch (error) {
      setResult({
        success: false,
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        data: error
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <AdminNav
        title="Email Test"
        description="Test email sending and templates"
      />
      <div className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-slate-800 rounded-xl shadow-2xl p-8">

          {/* DNS Setup Notice */}
          <div className="bg-blue-900 border border-blue-700 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Coffee className="w-6 h-6 text-blue-400 flex-shrink-0" />
              <div>
                <h3 className="text-blue-200 font-semibold mb-2">Testing with updates.birthday.coffee domain</h3>
                <p className="text-blue-300 text-sm">
                  Emails will be sent from <strong>gifts@updates.birthday.coffee</strong> to <strong>hi@birthday.coffee</strong>
                </p>
                <p className="text-blue-300 text-xs mt-2">
                  Domain is validated in Resend ✓
                </p>
              </div>
            </div>
          </div>

          {/* Test Form */}
          <div className="bg-slate-700 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-white mb-4">Test Email Configuration</h2>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-slate-300 text-sm mb-1">To Email</label>
                <input
                  type="email"
                  value={formData.to}
                  onChange={(e) => setFormData({...formData, to: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                  disabled={sending}
                />
              </div>
              <div>
                <label className="block text-slate-300 text-sm mb-1">Recipient Name</label>
                <input
                  type="text"
                  value={formData.recipientName}
                  onChange={(e) => setFormData({...formData, recipientName: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                  disabled={sending}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-slate-300 text-sm mb-1">Sender Name</label>
                <input
                  type="text"
                  value={formData.senderName}
                  onChange={(e) => setFormData({...formData, senderName: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                  disabled={sending}
                />
              </div>
              <div>
                <label className="block text-slate-300 text-sm mb-1">Gift Amount</label>
                <select
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: Number(e.target.value)})}
                  className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                  disabled={sending}
                >
                  <option value={5}>$5</option>
                  <option value={10}>$10</option>
                  <option value={15}>$15</option>
                  <option value={25}>$25</option>
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-slate-300 text-sm mb-1">Birthday Message</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="w-full px-4 py-3 bg-slate-600 border border-slate-500 rounded-lg text-white h-24"
                disabled={sending}
              />
            </div>

            <div className="mb-6">
              <label className="block text-slate-300 text-sm mb-1">Gift Code (auto-generated)</label>
              <input
                type="text"
                value={formData.giftCode}
                readOnly
                className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-yellow-400 font-mono"
              />
            </div>

            <button
              onClick={sendTestEmail}
              disabled={sending}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {sending ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending Email...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Send Test Email
                </>
              )}
            </button>
          </div>

          {/* Results */}
          {result && (
            <div className={`p-6 rounded-lg ${
              result.success
                ? 'bg-green-900 border border-green-700'
                : 'bg-red-900 border border-red-700'
            }`}>
              <div className="flex items-start gap-3">
                {result.success ? (
                  <Check className="w-6 h-6 text-green-400 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <h3 className={`font-semibold mb-2 ${
                    result.success ? 'text-green-200' : 'text-red-200'
                  }`}>
                    {result.success ? 'Email Sent Successfully!' : 'Email Failed'}
                  </h3>
                  <p className={result.success ? 'text-green-300' : 'text-red-300'}>
                    {result.message}
                  </p>
                  {result.data && (
                    <div className="mt-3">
                      <details className="cursor-pointer">
                        <summary className="text-sm text-slate-400 hover:text-slate-300">
                          View Response Details
                        </summary>
                        <pre className="mt-2 p-3 bg-slate-900 rounded text-xs text-slate-300 overflow-auto">
                          {JSON.stringify(result.data, null, 2)}
                        </pre>
                      </details>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* DNS Setup Guide */}
          <div className="mt-8 p-6 bg-slate-700 rounded-lg">
            <h3 className="text-white font-semibold mb-3">📝 DNS Setup for updates.birthday.coffee</h3>
            <p className="text-slate-300 text-sm mb-4">
              To send emails from your subdomain, add these DNS records:
            </p>
            <div className="space-y-3">
              <div className="bg-slate-800 rounded p-3">
                <p className="text-slate-400 text-xs mb-1">SPF Record (TXT)</p>
                <code className="text-green-400 text-xs">
                  v=spf1 include:amazonses.com ~all
                </code>
              </div>
              <div className="bg-slate-800 rounded p-3">
                <p className="text-slate-400 text-xs mb-1">DKIM (will be provided by Resend)</p>
                <code className="text-green-400 text-xs">
                  Check Resend dashboard for DKIM records after domain verification
                </code>
              </div>
              <p className="text-slate-400 text-xs">
                Visit <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                  Resend Domains
                </a> to verify updates.birthday.coffee
              </p>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}